import { defineStore } from 'pinia';
import { computed, effectScope, ref, watch } from 'vue';
import { notificationsApi } from '../services/api';
import { currentSurfaceAnnounces, intakeNotice } from '../services/noticeSurface';
import { useSocketStore } from './socket';

export interface Notification {
  notification_id: string;
  user_id: string;
  notification_type: string;
  title: string;
  message: string;
  event_id?: string;
  is_read: boolean;
  read_at?: string;
  created_at: string;
  /** TTS message to read aloud on the client */
  tts_message?: string;
  /** Notification level: critical, warning, info */
  level?: 'critical' | 'warning' | 'info';
  /** Optional alert sound to play */
  sound?: string;
  /** Associated device id */
  device_id?: string;
  /** Associated camera id — when set, the notification toast shows a live camera preview */
  camera_id?: string;
}

export interface NotificationPreference {
  preference_id: string;
  user_id: string;
  notification_type: string;
  push_enabled: boolean;
  in_app_enabled: boolean;
  email_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export const useNotificationStore = defineStore('notifications', () => {
  const notifications = ref<Notification[]>([]);
  const unreadCount = ref(0);
  const preferences = ref<NotificationPreference[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const initialized = ref(false);
  /**
   * Bumped only for notifications pushed LIVE over the socket (never for REST
   * fetches) — the list/badge side of a live push, on every surface.
   */
  const liveSeq = ref(0);

  /**
   * The one notice THIS surface owes an announcement for: a balloon, the alert
   * tone and the utterance (cards t_4677fac1, t_f0ea3681).
   *
   * One hub event reaches every surface of that hub — in the desktop shell each
   * tab is its own renderer with its own socket — and each of them used to
   * announce it, so the operator saw the balloon twice (in the tab he was in and
   * in the home tab) and heard TTS twice. The notice layer now decides: the
   * surface the operator is looking at announces, the others hold the notice and
   * bump the unread badge — and the desktop launcher home announces nothing at
   * all, whether it is the operator's surface or not (`services/noticeSurface.ts`
   * holds the decision and its test). `NotificationToast` watches `announceSeq`
   * — not `liveSeq` — so a surface that only badges never paints or speaks.
   */
  const announcement = ref<Notification | null>(null);
  const announceSeq = ref(0);

  const socketStore = useSocketStore();

  /**
   * Detached effect scope that owns the socket-listener watchers.
   *
   * `initialize()` is usually first called from a component watcher with
   * `{ immediate: true }` (Servers.vue), so a `watch()` created inside it would
   * be registered in THAT component's effect scope and silently disposed when
   * the component unmounts (e.g. after entering a hub). The notification
   * listener then stayed bound to the very first socket of the session and no
   * balloon/sound/TTS was ever played again after a hub switch. A detached
   * scope keeps these watchers alive for the lifetime of the store.
   */
  const listenerScope = effectScope(true);

  // Computed
  const unreadNotifications = computed(() => notifications.value.filter((n) => !n.is_read));

  const notificationsByType = computed(() => {
    const byType: Record<string, Notification[]> = {};
    notifications.value.forEach((n) => {
      if (!byType[n.notification_type]) {
        byType[n.notification_type] = [];
      }
      byType[n.notification_type].push(n);
    });
    return byType;
  });

  const notificationTypes = computed(() => Array.from(new Set(notifications.value.map((n) => n.notification_type))));

  /**
   * Initialize the store and setup Socket.IO listeners
   */
  function initialize() {
    if (initialized.value) return;
    initialized.value = true;

    // Register immediately if socket is already connected
    _registerSocketListener();

    // Re-register whenever the socket instance changes (hub switch, cloud/local
    // transport change) or reconnects. Runs in a detached scope so the watchers
    // survive whichever component triggered the first initialize().
    listenerScope.run(() => {
      watch(
        () => socketStore.socket,
        (newSocket) => {
          if (newSocket) {
            _registerSocketListener();
          }
        },
      );
      watch(
        () => socketStore.connected,
        (connected) => {
          if (connected) {
            _registerSocketListener();
          }
        },
      );
    });
  }

  /**
   * Make sure the live socket carries the notification listener (idempotent).
   * Safe to call from any component (e.g. the global toast) to self-heal after
   * a socket swap that happened while nothing was watching.
   */
  function ensureSocketListener() {
    _registerSocketListener();
  }

  /** Register the notification listener on the current socket (idempotent) */
  function _registerSocketListener() {
    const s = socketStore.socket;
    if (!s) return;
    // Remove first to prevent duplicate listeners on reconnect
    s.off('notification', handleIncomingNotification);
    s.on('notification', handleIncomingNotification);
    console.log('[NotificationStore] Socket.IO notification listener registered');
  }

  /**
   * Cleanup Socket.IO listeners
   */
  function cleanup() {
    if (socketStore.socket) {
      socketStore.socket.off('notification', handleIncomingNotification);
    }
    initialized.value = false;
  }

  /**
   * Handle incoming real-time notification from Socket.IO
   */
  function handleIncomingNotification(data: Partial<Notification> & { type?: string }) {
    console.log('[NotificationStore] Received real-time notification:', data);

    // One event, one announcement across the surfaces of this hub (cards
    // t_4677fac1, t_f0ea3681): `intakeNotice` is the whole decision, and the
    // count it protects is asserted in `services/noticeSurface.test.ts`. The
    // desktop launcher home may never announce (`launcherSurface` in the facts):
    // it holds no hub socket at all, so a notice reaching it is stored, never
    // painted, played or spoken.
    const known = notifications.value.some((n) => n.notification_id === data.notification_id);
    const intake = intakeNotice({ known, announcingSurface: currentSurfaceAnnounces() });
    if (!intake.store) return;

    const notification: Notification = {
      notification_id:
        data.notification_id ||
        (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2) + Date.now().toString(36)),
      user_id: data.user_id || '',
      notification_type: data.notification_type || data.type || 'info',
      title: data.title ?? '',
      message: data.message ?? '',
      event_id: data.event_id,
      is_read: false,
      created_at: data.created_at || new Date().toISOString(),
      tts_message: data.tts_message,
      level: data.level,
      sound: data.sound,
      device_id: data.device_id,
    };

    notifications.value.unshift(notification);
    unreadCount.value += 1;
    liveSeq.value += 1;

    console.log('[NotificationStore] New notification added');

    // The announcement (balloon + tone + utterance) belongs to ONE surface:
    // the one the operator is looking at. Every other surface keeps the badge.
    if (intake.announce) {
      announcement.value = notification;
      announceSeq.value += 1;
    }
  }

  /**
   * Fetch notifications from API
   */
  async function fetchNotifications(params?: { limit?: number; isRead?: boolean; type?: string }) {
    loading.value = true;
    error.value = null;

    try {
      const data = await notificationsApi.getAll(params);
      notifications.value = data;

      // Update unread count
      await updateUnreadCount();

      console.log('[NotificationStore] Fetched notifications:', data.length);
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err.message || 'Failed to fetch notifications';
      console.error('[NotificationStore] Error fetching notifications:', err);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update unread notification count
   */
  async function updateUnreadCount() {
    try {
      const data = await notificationsApi.getUnreadCount();
      unreadCount.value = data.count || 0;
    } catch (err) {
      console.error('[NotificationStore] Error updating unread count:', err);
    }
  }

  /**
   * Mark notification as read
   */
  async function markAsRead(notificationId: string) {
    try {
      await notificationsApi.markAsRead(notificationId);

      // Update local state
      const notification = notifications.value.find((n) => n.notification_id === notificationId);
      if (notification && !notification.is_read) {
        notification.is_read = true;
        notification.read_at = new Date().toISOString();
        unreadCount.value = Math.max(0, unreadCount.value - 1);
      }

      console.log('[NotificationStore] Marked notification as read:', notificationId);
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err.message || 'Failed to mark notification as read';
      console.error('[NotificationStore] Error marking as read:', err);
      throw err;
    }
  }

  /**
   * Mark all notifications as read
   */
  async function markAllAsRead() {
    try {
      await notificationsApi.markAllAsRead();

      // Update all local notifications
      notifications.value.forEach((n) => {
        if (!n.is_read) {
          n.is_read = true;
          n.read_at = new Date().toISOString();
        }
      });

      unreadCount.value = 0;

      console.log('[NotificationStore] Marked all notifications as read');
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err.message || 'Failed to mark all as read';
      console.error('[NotificationStore] Error marking all as read:', err);
      throw err;
    }
  }

  /**
   * Delete notification
   */
  async function deleteNotification(notificationId: string) {
    try {
      await notificationsApi.delete(notificationId);

      // Remove from local state
      const index = notifications.value.findIndex((n) => n.notification_id === notificationId);
      if (index !== -1) {
        const wasUnread = !notifications.value[index].is_read;
        notifications.value.splice(index, 1);
        if (wasUnread) {
          unreadCount.value = Math.max(0, unreadCount.value - 1);
        }
      }

      console.log('[NotificationStore] Deleted notification:', notificationId);
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err.message || 'Failed to delete notification';
      console.error('[NotificationStore] Error deleting notification:', err);
      throw err;
    }
  }

  /**
   * Delete all notifications
   */
  async function deleteAll() {
    try {
      await notificationsApi.deleteAll();

      // Clear local state
      notifications.value = [];
      unreadCount.value = 0;

      console.log('[NotificationStore] Deleted all notifications');
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err.message || 'Failed to delete all notifications';
      console.error('[NotificationStore] Error deleting all:', err);
      throw err;
    }
  }

  /**
   * Fetch notification preferences
   */
  async function fetchPreferences() {
    try {
      const data = await notificationsApi.getPreferences();
      preferences.value = data;

      console.log('[NotificationStore] Fetched preferences');
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err.message || 'Failed to fetch preferences';
      console.error('[NotificationStore] Error fetching preferences:', err);
      throw err;
    }
  }

  /**
   * Update notification preferences
   */
  async function updatePreferences(prefs: {
    notificationType: string;
    pushEnabled?: boolean;
    inAppEnabled?: boolean;
    emailEnabled?: boolean;
  }) {
    try {
      await notificationsApi.updatePreferences(prefs);

      // Update local state
      const index = preferences.value.findIndex((p) => p.notification_type === prefs.notificationType);
      if (index !== -1) {
        if (prefs.pushEnabled !== undefined) preferences.value[index].push_enabled = prefs.pushEnabled;
        if (prefs.inAppEnabled !== undefined) preferences.value[index].in_app_enabled = prefs.inAppEnabled;
        if (prefs.emailEnabled !== undefined) preferences.value[index].email_enabled = prefs.emailEnabled;
      }

      console.log('[NotificationStore] Updated preferences');
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err.message || 'Failed to update preferences';
      console.error('[NotificationStore] Error updating preferences:', err);
      throw err;
    }
  }

  /**
   * Reset store state
   */
  function reset() {
    notifications.value = [];
    unreadCount.value = 0;
    preferences.value = [];
    loading.value = false;
    error.value = null;
    cleanup();
  }

  return {
    // State
    notifications,
    unreadCount,
    preferences,
    loading,
    error,

    // Computed
    unreadNotifications,
    notificationsByType,
    notificationTypes,

    // Actions
    initialize,
    ensureSocketListener,
    cleanup,
    fetchNotifications,
    updateUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAll,
    fetchPreferences,
    updatePreferences,
    reset,
    handleIncomingNotification,

    // Live-push sequences: `liveSeq` = the list/badge side (every surface),
    // `announceSeq` + `announcement` = the announcement this surface owes
    // (NotificationToast watches these)
    liveSeq,
    announcement,
    announceSeq,
  };
});
